import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Minus, Plus, Heart, Truck, Shield, ArrowLeft, Play } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { SocialProof, WhatsAppButton, SeasonHighlight } from '../components/MarketFeatures';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProductPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [producer, setProducer] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const productRes = await axios.get(`${API_URL}/api/products/${productId}`);
      setProduct(productRes.data);
      
      if (productRes.data.producer_id) {
        const producerRes = await axios.get(`${API_URL}/api/producers/${productRes.data.producer_id}`);
        setProducer(producerRes.data);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addItem(product, quantity);
    toast.success(`${product.name} sepete eklendi!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3625]"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-[#1A3625] mb-4">Ürün bulunamadı</h2>
          <Link to="/market" className="btn-primary">
            Markete Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="product-page">
      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[#E5DAC8]">
        <div className="container-custom">
          <Link to="/market" className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#1A3625] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Markete Dön
          </Link>
        </div>
      </div>

      {/* Product Details */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="aspect-square rounded-3xl overflow-hidden bg-white shadow-lg">
                <img 
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex flex-col"
            >
              {/* Season Highlight */}
              {product.category === 'bal' && (
                <div className="mb-4">
                  <SeasonHighlight />
                </div>
              )}
              
              <span className="label-uppercase text-[#D4AF37] mb-2">{product.category}</span>
              <h1 className="text-3xl md:text-4xl text-[#1A3625] mb-4">{product.name}</h1>
              
              {/* Social Proof */}
              <div className="mb-4">
                <SocialProof productId={product.id} />
              </div>
              
              <p className="text-base text-[#5C5C5C] mb-6">{product.description}</p>
              
              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-3xl font-semibold text-[#D4AF37]">₺{product.price}</span>
                <span className="text-[#5C5C5C]">/ {product.unit}</span>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
                <div className="flex items-center justify-center gap-4 bg-[#F9F8F6] rounded-full p-2">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-[#E5DAC8] transition-colors"
                    data-testid="quantity-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-medium" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-[#E5DAC8] transition-colors"
                    data-testid="quantity-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                
                <button
                  onClick={handleAddToCart}
                  disabled={!product.in_stock}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Sepete Ekle
                </button>
              </div>
              
              {/* WhatsApp Order Button */}
              <div className="mb-8">
                <WhatsAppButton productName={product.name} productPrice={product.price} />
              </div>

              {/* Benefits */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 text-[#5C5C5C]">
                  <Truck className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-base">500₺ üzeri ücretsiz kargo</span>
                </div>
                <div className="flex items-center gap-3 text-[#5C5C5C]">
                  <Shield className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-base">%100 Doğal</span>
                </div>
              </div>

              {/* Impact */}
              {product.impact && (
                <div className="bg-[#1A3625]/5 rounded-2xl p-6 mb-8">
                  <h3 className="font-medium text-[#1A3625] mb-2 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-[#D4AF37]" />
                    Satın Alarak Destekliyorsunuz
                  </h3>
                  <p className="text-[#5C5C5C]">{product.impact}</p>
                </div>
              )}

              {/* Producer */}
              {producer && (
                <Link 
                  to={`/market/uretici/${producer.id}`}
                  className="card-premium flex items-center gap-4 hover:shadow-lg transition-shadow"
                  data-testid="producer-link"
                >
                  <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src={producer.image}
                      alt={producer.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-base text-[#5C5C5C]">Üretici</p>
                    <p className="font-medium text-[#1A3625]">{producer.name}</p>
                    <p className="text-base text-[#D4AF37]">{producer.location}</p>
                  </div>
                </Link>
              )}
            </motion.div>
          </div>

          {/* Story Section */}
          {product.story && (
            <motion.div
              className="mt-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#1A3625] text-white rounded-3xl p-10 md:p-14 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2d5a3f]/30 rounded-full blur-3xl" />
                <div className="relative">
                  <span className="label-uppercase text-[#D4AF37] mb-4 block">Hikaye</span>
                  <h2 className="text-2xl md:text-3xl mb-6">Bu Ürünün Hikayesi</h2>
                  <div className="space-y-4">
                    {product.story.split('\n\n').filter(p => p.trim()).map((paragraph, i) => (
                      <p key={i} className="text-white/85 text-base md:text-lg leading-relaxed">{paragraph.trim()}</p>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};
